
import React, { useState } from 'react';
import { apiUrl as mk } from '../utils/api';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import SuccessModal from './SuccessModal'; // Import the SuccessModal component
import AuthSidebar from './AuthSidebar'; // Import the reusable sidebar
import { Link } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import logoSrc from '../engazeiq_logo.png'; // Import the logo

// --- Styles for Layout ---

const CreateUserContainer = styled.div`
  display: flex;
  height: 100vh;
  width: 100vw;
  background-color: var(--secondary-color, #f0f4f8);
`;

const CreateUserMain = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 20px;
  background-color: white;
  overflow-y: auto;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-start;
    padding-top: 50px;
  }
`;

// --- Original Form Styles (Kept) ---

const Form = styled.form`
  background-color: white;
  padding: 21px;
  border-radius: 8px;
  width: 100%;
  max-width: 400px;
`;


const Input = styled.input`
  width: 100%;
  padding: 12px;
  margin-bottom: 16px;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box;
`;

const CreateButton = styled.button`
  background-color: #FF8000;
  color: white;
  padding: 12px 135px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background-color: #FF8000;
  }
`;

const PasswordWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const ToggleButton = styled.button`
  position: absolute;
  right: 12px;
  top: 40%; // Adjusted for margin-bottom of Input
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  
  &:hover {
    color: #333;
  }
`;

// --- Original React Component Logic (Kept) ---

const CreateUser: React.FC = () => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [role, setRole] = useState('Viewer');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();


  const BottomText = styled.p`
  margin-top: 16px;
  font-size: 14px;
  color: #555;
  text-align: center;
`;

  const StyledLink = styled(Link)`
  color: #FF8000;
  text-decoration: none;
  font-weight: bold;

  &:hover {
    text-decoration: underline;
  }
`;


  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    console.log("CreateUser: handleSubmit initiated"); // Added log

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    const userData = {
      username: username,
      email,
      password,
      confirm_password: confirmPassword,
      company_name: companyName,
      invite_code: inviteCode,
      role,
    };

    try {
      const response = await fetch(mk('/api/auth/register'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        let errorMessage = 'Could not create user';
        if (errorData.detail) {
          if (Array.isArray(errorData.detail)) {
            errorMessage = errorData.detail.map((err: any) => err.msg).join(', ');
          } else if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          }
        }
        throw new Error(errorMessage);
      }

      await response.json();
      console.log("CreateUser: Registration successful, setting showSuccessModal to true"); // Added log

      setUsername('');
      setEmail('');
      setPassword('');
      setConfirmPassword('');
      setCompanyName('');
      setInviteCode('');
      setRole('Viewer');
      setError('');

      setShowSuccessModal(true);
    } catch (error: unknown) {
      if (error instanceof Error) {
        console.error('CreateUser: Registration failed:', error.message); // Added log
        setError(error.message);
      }
      else {
        console.error('CreateUser: Unexpected error', error); // Added log
        setError('An unexpected error occurred.');
      }
    }
  };

  const handleModalClose = () => {
    console.log("CreateUser: handleModalClose called"); // Added log
    setShowSuccessModal(false);
    navigate('/login');
  };

  return (
    <CreateUserContainer>
      <AuthSidebar /> {/* Use the reusable sidebar component */}

      <CreateUserMain>

        <img
          src={logoSrc}
          alt="EngazeIQ Logo"
          style={{ display: 'block', height: '30px', marginTop: '5px' }} // Center using auto margins
        />
        <span style={{ marginBottom: '1px' }}>Create a <span style={{ fontWeight: 700 }}>New User</span> Accouunt </span>

        <Form onSubmit={handleSubmit}>

          {error && <p style={{ color: 'red', marginBottom: '12px' }}>{error}</p>}
          <Input
            type="text"
            placeholder="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
          <Input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <PasswordWrapper>
            <Input
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{ paddingRight: "40px" }}
            />
            <ToggleButton
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </ToggleButton>
          </PasswordWrapper>

          <PasswordWrapper>
            <Input
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Confirm Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              style={{ paddingRight: "40px" }}
            />
            <ToggleButton
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              aria-label={showConfirmPassword ? "Hide password" : "Show password"}
            >
              {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </ToggleButton>
          </PasswordWrapper>
          <Input
            type="text"
            placeholder="Company Name"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
          />

          <Input
            type="text"
            placeholder="Organization Invite Code (Optional)"
            value={inviteCode}
            onChange={(e) => setInviteCode(e.target.value)}
          />
          {/* Role selection removed - defaults to Viewer */}
          <CreateButton type="submit">Create Account</CreateButton>
          <BottomText>
            Already have an account? <StyledLink to="/login">Sign In</StyledLink>
          </BottomText>
        </Form>

      </CreateUserMain>

      {showSuccessModal && (
        <SuccessModal message="User created successfully!" onClose={handleModalClose} />
      )}
    </CreateUserContainer>
  );
};

export default CreateUser;
